import { Fragment, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FaCheck, FaRegFileAlt, FaTrash } from "react-icons/fa";
import { IoIosCloudUpload } from "react-icons/io";
import moment from "moment";

import useDebounce from "../../../../hooks/useDebounce";
import ErrorPopup from "../../../../common/particles.jsx/ErrorPopup";
import {
  getCity,
  getDistrict,
  getProvince,
  getSubdistrict,
  uploadImage,
  deleteImage,
} from "../../../../../services/authApi";

const AddRegisterSarana = ({
  formik,
  dataBusiness,
  isFetchingBusiness,
  dataBank,
  isFetchingBank,
  isSameAddress,
  setIsSameAddress,
}) => {
  const [errorMessage, setErrorMessage] = useState("");

  const debounceProvince = useDebounce(
    formik.values.company.business.province,
    500
  );
  const debounceProvinceNpwp = useDebounce(
    formik.values.company.npwp.province,
    500
  );
  const debounceCity = useDebounce(formik.values.company.business.city, 500);
  const debounceCityNpwp = useDebounce(formik.values.company.npwp.city, 500);
  const debounceDistrict = useDebounce(
    formik.values.company.business.district,
    500
  );
  const debounceDistrictNpwp = useDebounce(
    formik.values.company.npwp.district,
    500
  );

  const handleClickPreview = (url) => {
    window.open(url, "_blank");
  };

  const { data: dataProvince, isFetching: isFetchingProvince } = useQuery({
    queryKey: ["province"],
    queryFn: () => getProvince(),
    retryDelay: 60000, // one minute delay
    refetchOnWindowFocus: false,
  });
  const { data: dataProvinceNpwp, isFetching: isFetchingProvinceNpwp } =
    useQuery({
      queryKey: ["province"],
      queryFn: () => getProvince(),
      retryDelay: 60000, // one minute delay
      refetchOnWindowFocus: false,
    });

  const { data: dataCity, isFetching: isFetchingCity } = useQuery({
    queryKey: ["city", debounceProvince],
    queryFn: () => getCity(debounceProvince),
    enabled: debounceProvince !== "",
    retryDelay: 60000, // one minute delay
    refetchOnWindowFocus: false,
  });

  const { data: dataCityNpwp, isFetching: isFetchingCityNpwp } = useQuery({
    queryKey: ["city", debounceProvinceNpwp],
    queryFn: () => getCity(debounceProvinceNpwp),
    enabled: debounceProvince !== "",
    retryDelay: 60000, // one minute delay
    refetchOnWindowFocus: false,
  });

  const { data: dataDistrict, isFetching: isFetchingDistrict } = useQuery({
    queryKey: ["district", debounceCity],
    queryFn: () => getDistrict(debounceCity),
    enabled: debounceCity !== "",
    retryDelay: 60000, // one minute delay
    refetchOnWindowFocus: false,
  });

  const { data: dataDistrictNpwp, isFetching: isFetchingDistrictNpwp } =
    useQuery({
      queryKey: ["district", debounceCityNpwp],
      queryFn: () => getDistrict(debounceCityNpwp),
      enabled: debounceCityNpwp !== "",
      retryDelay: 60000, // one minute delay
      refetchOnWindowFocus: false,
    });

  const { data: dataSubdistrict, isFetching: isFetchingSubdistrict } = useQuery(
    {
      queryKey: ["subdistrict", debounceDistrict],
      queryFn: () => getSubdistrict(debounceDistrict),
      enabled: debounceDistrict !== "",
      retryDelay: 60000, // one minute delay
      refetchOnWindowFocus: false,
    }
  );

  const { data: dataSubdistrictNpwp, isFetching: isFetchingSubdistrictNpwp } =
    useQuery({
      queryKey: ["subdistrict", debounceDistrictNpwp],
      queryFn: () => getSubdistrict(debounceDistrictNpwp),
      enabled: debounceDistrictNpwp !== "",
      retryDelay: 60000, // one minute delay
      refetchOnWindowFocus: false,
    });

  const { mutate: mutateUploadNIB, isPending: isPendingUploadNIB } =
    useMutation({
      mutationFn: uploadImage,
      onSuccess: (data) => {
        formik.setFieldValue("company.files.nib", data.url);
        formik.setFieldValue("company.files.type_nib", data.data_type);
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateDeleteNIB, isPending: isPendingDeleteNIB } =
    useMutation({
      mutationFn: deleteImage,
      onSuccess: () => {
        formik.setFieldValue("company.files.nib", "");
        formik.setFieldValue("company.files.type_nib", "");
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateUploadSIA, isPending: isPendingUploadSIA } =
    useMutation({
      mutationFn: uploadImage,
      onSuccess: (data) => {
        formik.setFieldValue("company.files.izin_sarana", data.url);
        formik.setFieldValue("company.files.type_izin_sarana", data.data_type);
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateDeleteSIA, isPending: isPendingDeleteSIA } =
    useMutation({
      mutationFn: deleteImage,
      onSuccess: () => {
        formik.setFieldValue("company.files.izin_sarana", "");
        formik.setFieldValue("company.files.type_izin_sarana", "");
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateUploadStempel, isPending: isPendingUploadStempel } =
    useMutation({
      mutationFn: uploadImage,
      onSuccess: (data) => {
        formik.setFieldValue("company.files.stempel_sarana", data.url);
        formik.setFieldValue(
          "company.files.type_stempel_sarana",
          data.data_type
        );
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateDeleteStempel, isPending: isPendingDeleteStempel } =
    useMutation({
      mutationFn: deleteImage,
      onSuccess: () => {
        formik.setFieldValue("company.files.stempel_sarana", "");
        formik.setFieldValue("company.files.type_stempel_sarana", "");
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateUploadNPWP, isPending: isPendingUploadNPWP } =
    useMutation({
      mutationFn: uploadImage,
      onSuccess: (data) => {
        formik.setFieldValue("company.files.npwp", data.url);
        formik.setFieldValue("company.files.type_npwp", data.data_type);
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const { mutate: mutateDeleteNPWP, isPending: isPendingDeleteNPWP } =
    useMutation({
      mutationFn: deleteImage,
      onSuccess: () => {
        formik.setFieldValue("company.files.npwp", "");
        formik.setFieldValue("company.files.type_npwp", "");
      },
      onError: (err) =>
        setErrorMessage(err.message.id ? err.message.id : err.message),
    });

  const SuccessfulUploadComponent = ({ title, value, type, fileType }) => {
    return (
      <div>
        <div className="w-full bg-blue-500 text-white py-3 px-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaRegFileAlt className="text-[16px]" />
            <p>{title}</p>
          </div>
          <FaTrash
            className="text-red-500 hover:text-gray-300 cursor-pointer text-[14px]"
            onClick={() => {
              if (type === "nib") mutateDeleteNIB({ value, fileType });
              if (type === "sia") mutateDeleteSIA({ value, fileType });
              if (type === "stempel") mutateDeleteStempel({ value, fileType });
              if (type === "npwp") mutateDeleteNPWP({ value, fileType });
            }}
          />
        </div>
        <div
          className={`w-full border-dashed p-4 border-b-2 border-l-2 border-r-2 border-gray-300 rounded-b-lg bg-white ${
            value === ""
              ? "cursor-default"
              : "cursor-pointer hover:text-gray-300"
          } flex flex-col justify-center items-center`}
          onClick={() => {
            if (value !== "") handleClickPreview(value);
          }}
        >
          {(type === "nib" && isPendingDeleteNIB) ||
          (type === "sia" && isPendingDeleteSIA) ||
          (type === "stempel" && isPendingDeleteStempel) ||
          (type === "npwp" && isPendingDeleteNPWP) ? (
            <CircularProgress />
          ) : value === "" ? null : (
            <FaCheck className="text-[64px] text-blue-500" />
          )}
          <p className="mt-2">
            {isPendingDeleteNIB ||
            isPendingDeleteSIA ||
            isPendingDeleteStempel ||
            isPendingDeleteNPWP
              ? null
              : value === ""
              ? "Tidak ada berkas"
              : "Berhasil Diupload!"}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div>
      <FormControl fullWidth>
        <FormLabel>Bidang Usaha</FormLabel>
        {
          <Select
            sx={{ width: "100%", marginBottom: 2 }}
            value={formik.values.company.business_field_id}
            onChange={(e) =>
              formik.setFieldValue(
                "company.business_field_id",
                Number(e.target.value)
              )
            }
          >
            {dataBusiness && !isFetchingBusiness ? (
              dataBusiness.map((item) => (
                <MenuItem value={item.id}>{item.name}</MenuItem>
              ))
            ) : (
              <MenuItem value={0} disabled>
                Pilih Bidang Usaha
              </MenuItem>
            )}
          </Select>
        }
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Nama Usaha</FormLabel>
        <TextField
          fullWidth
          sx={{ marginBottom: 2 }}
          value={formik.values.company.name}
          onChange={(e) => formik.setFieldValue("company.name", e.target.value)}
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>No. NPWP Usaha</FormLabel>
        <TextField
          fullWidth
          sx={{ marginBottom: 2 }}
          value={formik.values.company.npwp.number}
          onChange={(e) => {
            if (!isNaN(Number(e.target.value)) && e.target.value.length <= 15)
              formik.setFieldValue("company.npwp.number", e.target.value);
          }}
          error={formik.values.company.npwp.number.length < 15}
          helperText={
            formik.values.company.npwp.number.length < 15
              ? "No. NPWP usaha tidak boleh kurang dari 15 karakter"
              : null
          }
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Alamat NPWP</FormLabel>
        <TextField
          fullWidth
          sx={{ marginBottom: 2 }}
          multiline
          rows={3}
          value={formik.values.company.npwp.address}
          onChange={(e) =>
            formik.setValues({
              ...formik.values,
              company: {
                ...formik.values.company,
                npwp: {
                  ...formik.values.company.npwp,
                  address: e.target.value,
                },
                business: {
                  ...formik.values.company.business,
                  address: isSameAddress
                    ? e.target.value
                    : formik.values.company.business.province,
                },
              },
            })
          }
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Provinsi</FormLabel>
        <Autocomplete
          fullWidth
          loading={isFetchingProvinceNpwp}
          disablePortal
          options={dataProvinceNpwp ? dataProvinceNpwp : []}
          value={
            formik.values.company.npwp.province !== "" && dataProvinceNpwp
              ? dataProvinceNpwp.find(
                  (prov) =>
                    prov.province === formik.values.company.npwp.province
                )
              : null
          }
          onChange={(_, option) => {
            formik.setValues({
              ...formik.values,
              company: {
                ...formik.values.company,
                npwp: {
                  ...formik.values.company.npwp,
                  province: option ? option.province : "",
                  city: "",
                  district: "",
                  subdistrict: "",
                  postalcode: "",
                  npwp_geography_id: 0,
                },
                business: {
                  ...formik.values.company.business,
                  province:
                    option && isSameAddress
                      ? option.province
                      : formik.values.company.business.province,
                  city: isSameAddress
                    ? ""
                    : formik.values.company.business.city,
                  district: isSameAddress
                    ? ""
                    : formik.values.company.business.district,
                  subdistrict: isSameAddress
                    ? ""
                    : formik.values.company.business.subdistrict,
                  postalcode: isSameAddress
                    ? ""
                    : formik.values.company.business.postalcode,
                  business_geography_id: isSameAddress
                    ? 0
                    : formik.values.company.business.business_geography_id,
                },
              },
            });
          }}
          getOptionLabel={(option) =>
            option.province ? option.province : "N/A"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ marginBottom: 2 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {isFetchingProvince ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          )}
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Kota</FormLabel>
        <Autocomplete
          fullWidth
          loading={isFetchingCityNpwp}
          disablePortal
          disabled={formik.values.company.npwp.province === "" || !dataCityNpwp}
          options={dataCityNpwp ? dataCityNpwp : []}
          value={
            formik.values.company.npwp.city !== "" && dataCityNpwp
              ? dataCityNpwp.find(
                  (city) => city.city === formik.values.company.npwp.city
                )
              : null
          }
          onChange={(_, option) => {
            formik.setValues({
              ...formik.values,
              company: {
                ...formik.values.company,
                npwp: {
                  ...formik.values.company.npwp,
                  city: option ? option.city : "",
                  district: "",
                  subdistrict: "",
                  postalcode: "",
                  npwp_geography_id: 0,
                },
                business: {
                  ...formik.values.company.business,
                  city:
                    option && isSameAddress
                      ? option.city
                      : formik.values.company.business.city,
                  district: isSameAddress
                    ? ""
                    : formik.values.company.business.district,
                  subdistrict: isSameAddress
                    ? ""
                    : formik.values.company.business.subdistrict,
                  postalcode: isSameAddress
                    ? ""
                    : formik.values.company.business.postalcode,
                  business_geography_id: isSameAddress
                    ? 0
                    : formik.values.company.business.business_geography_id,
                },
              },
            });
          }}
          getOptionLabel={(option) => (option.city ? option.city : "N/A")}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ marginBottom: 2 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {isFetchingCity ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          )}
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Kecamatan</FormLabel>
        <Autocomplete
          fullWidth
          loading={isFetchingDistrictNpwp}
          disablePortal
          disabled={formik.values.company.npwp.city === "" || !dataDistrictNpwp}
          options={dataDistrictNpwp ? dataDistrictNpwp : []}
          value={
            formik.values.company.npwp.district !== "" && dataDistrictNpwp
              ? dataDistrictNpwp.find(
                  (district) =>
                    district.district === formik.values.company.npwp.district
                )
              : null
          }
          onChange={(_, option) => {
            formik.setValues({
              ...formik.values,
              company: {
                ...formik.values.company,
                npwp: {
                  ...formik.values.company.npwp,
                  district: option ? option.district : "",
                  subdistrict: "",
                  postalcode: "",
                  npwp_geography_id: 0,
                },
                business: {
                  ...formik.values.company.business,
                  district:
                    option && isSameAddress
                      ? option.district
                      : formik.values.company.business.district,
                  subdistrict: isSameAddress
                    ? ""
                    : formik.values.company.business.subdistrict,
                  postalcode: isSameAddress
                    ? ""
                    : formik.values.company.business.postalcode,
                  business_geography_id: isSameAddress
                    ? 0
                    : formik.values.company.business.business_geography_id,
                },
              },
            });
          }}
          getOptionLabel={(option) =>
            option.district ? option.district : "N/A"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ marginBottom: 2 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {isFetchingDistrict ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          )}
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Kelurahan/Desa</FormLabel>
        <Autocomplete
          fullWidth
          loading={isFetchingSubdistrictNpwp}
          disablePortal
          disabled={
            formik.values.company.npwp.district === "" || !dataSubdistrictNpwp
          }
          options={dataSubdistrictNpwp ? dataSubdistrictNpwp : []}
          value={
            formik.values.company.npwp.subdistrict !== "" && dataSubdistrictNpwp
              ? dataSubdistrictNpwp.find(
                  (sub) =>
                    sub.sub_district === formik.values.company.npwp.subdistrict
                )
              : null
          }
          onChange={(_, option) => {
            formik.setValues({
              ...formik.values,
              company: {
                ...formik.values.company,
                npwp: {
                  ...formik.values.company.npwp,
                  subdistrict: option ? option.sub_district : "",
                  postalcode: option ? option.kode_pos : "",
                  npwp_geography_id: option ? option.id : "",
                },
                business: {
                  ...formik.values.company.business,
                  subdistrict:
                    option && isSameAddress
                      ? option.sub_district
                      : formik.values.company.business.subdistrict,
                  postalcode:
                    option && isSameAddress
                      ? option.kode_pos
                      : formik.values.company.business.postalcode,
                  business_geography_id:
                    option && isSameAddress
                      ? option.id
                      : formik.values.company.business.business_geography_id,
                },
              },
            });
          }}
          getOptionLabel={(option) =>
            option.sub_district ? option.sub_district : "N/A"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ marginBottom: 2 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {isFetchingSubdistrict ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          )}
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Kode Pos</FormLabel>
        <TextField
          disabled
          fullWidth
          sx={{ marginBottom: 2 }}
          value={formik.values.company.npwp.postalcode}
        />
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            checked={isSameAddress}
            onChange={(_, checked) => {
              setIsSameAddress(checked);
              if (checked)
                formik.setValues({
                  ...formik.values,
                  company: {
                    ...formik.values.company,
                    business: {
                      address: formik.values.company.npwp.address,
                      province: formik.values.company.npwp.province,
                      city: formik.values.company.npwp.city,
                      district: formik.values.company.npwp.district,
                      subdistrict: formik.values.company.npwp.subdistrict,
                      postalcode: formik.values.company.npwp.postalcode,
                      business_geography_id:
                        formik.values.company.npwp.npwp_geography_id,
                    },
                  },
                });
            }}
          />
        }
        label="Centang jika alamat usaha sama dengan NPWP"
        sx={{ marginBottom: 2 }}
      />
      {isSameAddress ? null : (
        <>
          <FormControl fullWidth>
            <FormLabel>Alamat Usaha</FormLabel>
            <TextField
              fullWidth
              sx={{ marginBottom: 2 }}
              multiline
              rows={3}
              value={formik.values.company.business.address}
              onChange={(e) =>
                formik.setFieldValue("company.business.address", e.target.value)
              }
            />
          </FormControl>
          <FormControl fullWidth>
            <FormLabel>Provinsi</FormLabel>
            <Autocomplete
              fullWidth
              loading={isFetchingProvince}
              disablePortal
              options={dataProvince ? dataProvince : []}
              value={
                formik.values.company.business.province !== "" && dataProvince
                  ? dataProvince.find(
                      (prov) =>
                        prov.province ===
                        formik.values.company.business.province
                    )
                  : null
              }
              onChange={(_, option) => {
                formik.setValues({
                  ...formik.values,
                  company: {
                    ...formik.values.company,
                    business: {
                      ...formik.values.company.business,
                      province: option ? option.province : "",
                      city: option ? option.city : "",
                      district: "",
                      subdistrict: "",
                      postalcode: "",
                      business_geography_id: 0,
                    },
                  },
                });
              }}
              getOptionLabel={(option) =>
                option.province ? option.province : "N/A"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ marginBottom: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {isFetchingProvince ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth>
            <FormLabel>Kota</FormLabel>
            <Autocomplete
              fullWidth
              loading={isFetchingCity}
              disablePortal
              disabled={
                formik.values.company.business.province === "" || !dataCity
              }
              options={dataCity ? dataCity : []}
              value={
                formik.values.company.business.city !== "" && dataCity
                  ? dataCity.find(
                      (city) =>
                        city.city === formik.values.company.business.city
                    )
                  : null
              }
              onChange={(_, option) => {
                formik.setValues({
                  ...formik.values,
                  company: {
                    ...formik.values.company,
                    business: {
                      ...formik.values.company.business,
                      city: option ? option.city : "",
                      district: "",
                      subdistrict: "",
                      postalcode: "",
                      business_geography_id: 0,
                    },
                  },
                });
              }}
              getOptionLabel={(option) => (option.city ? option.city : "N/A")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ marginBottom: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {isFetchingCity ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth>
            <FormLabel>Kecamatan</FormLabel>
            <Autocomplete
              fullWidth
              loading={isFetchingDistrict}
              disablePortal
              disabled={
                formik.values.company.business.city === "" || !dataDistrict
              }
              options={dataDistrict ? dataDistrict : []}
              value={
                formik.values.company.business.district !== "" && dataDistrict
                  ? dataDistrict.find(
                      (district) =>
                        district.district ===
                        formik.values.company.business.district
                    )
                  : null
              }
              onChange={(_, option) => {
                formik.setValues({
                  ...formik.values,
                  company: {
                    ...formik.values.company,
                    business: {
                      ...formik.values.company.business,
                      district: option ? option.district : "",
                      subdistrict: "",
                      postalcode: "",
                      business_geography_id: 0,
                    },
                  },
                });
              }}
              getOptionLabel={(option) =>
                option.district ? option.district : "N/A"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ marginBottom: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {isFetchingDistrict ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth>
            <FormLabel>Kelurahan/Desa</FormLabel>
            <Autocomplete
              fullWidth
              loading={isFetchingSubdistrict}
              disablePortal
              disabled={
                formik.values.company.business.district === "" ||
                !dataSubdistrict
              }
              options={dataSubdistrict ? dataSubdistrict : []}
              value={
                formik.values.company.business.subdistrict !== "" &&
                dataSubdistrict
                  ? dataSubdistrict.find(
                      (sub) =>
                        sub.sub_district ===
                        formik.values.company.business.subdistrict
                    )
                  : null
              }
              onChange={(_, option) => {
                formik.setValues({
                  ...formik.values,
                  company: {
                    ...formik.values.company,
                    business: {
                      ...formik.values.company.business,
                      subdistrict: option ? option.sub_district : "",
                      postalcode: option ? option.kode_pos : "",
                      business_geography_id: option ? option.id : 0,
                    },
                  },
                });
              }}
              getOptionLabel={(option) =>
                option.sub_district ? option.sub_district : "N/A"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ marginBottom: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {isFetchingSubdistrict ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth>
            <FormLabel>Kode Pos</FormLabel>
            <TextField
              disabled
              fullWidth
              sx={{ marginBottom: 2 }}
              value={formik.values.company.business.postalcode}
            />
          </FormControl>
        </>
      )}
      <FormControl fullWidth>
        <FormLabel>No. HP Usaha</FormLabel>
        <TextField
          placeholder="8100000000"
          fullWidth
          value={formik.values.company.phone}
          onChange={(e) => {
            if (!isNaN(Number(e.target.value)))
              formik.setFieldValue("company.phone", e.target.value);
          }}
          sx={{ marginBottom: 2 }}
          InputProps={{
            startAdornment: <InputAdornment>+62</InputAdornment>,
          }}
          error={
            formik.values.company.phone.length > 0 &&
            formik.values.company.phone[0] !== "8"
          }
          helperText={
            formik.values.company.phone.length > 0 &&
            formik.values.company.phone[0] !== "8"
              ? "No HP tidak valid"
              : null
          }
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Nomor Induk Berusaha</FormLabel>
        <TextField
          fullWidth
          sx={{ marginBottom: 2 }}
          value={formik.values.company.nib}
          onChange={(e) => {
            if (!isNaN(Number(e.target.value)) && e.target.value.length <= 13)
              formik.setFieldValue("company.nib", e.target.value);
          }}
          error={formik.values.company.nib.length < 13}
          helperText={
            formik.values.company.nib.length < 13
              ? "Nomor induk berusaha tidak boleh kurang dari 13 karakter"
              : null
          }
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Kode E-report/SIPNAP</FormLabel>
        <TextField
          fullWidth
          sx={{ marginBottom: 2 }}
          value={formik.values.company.sipnap_code}
          onChange={(e) =>
            formik.setFieldValue("company.sipnap_code", e.target.value)
          }
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Surat Izin Usaha</FormLabel>
        <TextField
          fullWidth
          sx={{ marginBottom: 2 }}
          value={formik.values.company.permission_letter_id}
          onChange={(e) =>
            formik.setFieldValue("company.permission_letter_id", e.target.value)
          }
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Masa Berlaku Surat Izin Usaha</FormLabel>
        <DesktopDatePicker
          fullWidth
          sx={{ marginBottom: 2 }}
          onChange={(date) =>
            formik.setFieldValue(
              "company.permission_letter_expired",
              moment(date).format("YYYY-MM-DD")
            )
          }
          value={
            formik.values.company.permission_letter_expired === ""
              ? null
              : moment(formik.values.company.permission_letter_expired)
          }
          renderInput={(params) => {
            return <TextField {...params} fullWidth />;
          }}
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Nomor Rekening Bank</FormLabel>
        <TextField
          fullWidth
          sx={{ marginBottom: 2 }}
          value={formik.values.company.bank_account_number}
          onChange={(e) => {
            if (!isNaN(Number(e.target.value)))
              formik.setFieldValue(
                "company.bank_account_number",
                e.target.value
              );
          }}
        />
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Nama Bank</FormLabel>
        <Select
          sx={{ width: "100%", marginBottom: 2 }}
          value={formik.values.company.bank}
          onChange={(e) => formik.setFieldValue("company.bank", e.target.value)}
        >
          {dataBank && !isFetchingBank ? (
            dataBank.map((item) => (
              <MenuItem value={item.name}>{item.name}</MenuItem>
            ))
          ) : (
            <MenuItem value={""} disabled>
              Pilih Bank
            </MenuItem>
          )}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <FormLabel>Nama Pemilik Rekening</FormLabel>
        <TextField
          fullWidth
          sx={{ marginBottom: 2 }}
          value={formik.values.company.bank_account_name}
          onChange={(e) =>
            formik.setFieldValue("company.bank_account_name", e.target.value)
          }
        />
      </FormControl>

      <p className="text-[16px] my-2 font-semibold">Unggah Semua Dokumen</p>

      {[
        {
          type: "nib",
          value: formik.values.company.files.nib,
          file_type: formik.values.company.files.type_nib,
          key: "company.files.nib",
          title: "Nomor Induk Berusaha",
          input_tag: "nib-upload",
        },
        {
          type: "sia",
          value: formik.values.company.files.izin_sarana,
          file_type: formik.values.company.files.type_izin_sarana,
          key: "company.files.izin_sarana",
          title: "Surat Izin Usaha",
          input_tag: "sia-upload",
        },
        {
          type: "stempel",
          value: formik.values.company.files.stempel_sarana,
          file_type: formik.values.company.files.type_stempel_sarana,
          key: "company.files.stempel_sarana",
          title: "Stampel Usaha",
          input_tag: "stempel-upload",
        },
        {
          type: "npwp",
          value: formik.values.company.files.npwp,
          file_type: formik.values.company.files.type_npwp,
          key: "company.files.npwp",
          title: "NPWP Usaha",
          input_tag: "npwp-upload",
        },
      ].map((item) => {
        return (
          <div className="mb-4">
            {item.value === "" ? (
              <>
                <input
                  type="file"
                  id={item.input_tag}
                  hidden
                  accept="image/jpeg, image/png ,image/jpg, application/pdf"
                  onChange={(e) => {
                    formik.setFieldValue(item.key, "");
                    if (item.type === "nib") mutateUploadNIB(e.target.files[0]);
                    if (item.type === "sia") mutateUploadSIA(e.target.files[0]);
                    if (item.type === "stempel")
                      mutateUploadStempel(e.target.files[0]);
                    if (item.type === "npwp")
                      mutateUploadNPWP(e.target.files[0]);
                  }}
                />
                <label for={item.input_tag} className="w-full">
                  <div className="w-full bg-blue-500 text-white py-3 px-4 rounded-t-lg flex items-center gap-2">
                    <FaRegFileAlt className="text-[16px]" />
                    <p>{item.title}</p>
                  </div>
                  <div className="w-full bg-white border-dashed p-4 border-b-2 border-l-2 border-r-2 border-gray-300 rounded-b-lg cursor-pointer hover:text-gray-300 flex flex-col justify-center items-center">
                    {(item.type === "nib" && isPendingUploadNIB) ||
                    (item.type === "sia" && isPendingUploadSIA) ||
                    (item.type === "stempel" && isPendingUploadStempel) ||
                    (item.type === "npwp" && isPendingUploadNPWP) ? (
                      <CircularProgress />
                    ) : (
                      <IoIosCloudUpload className="text-[64px] text-blue-500" />
                    )}
                    <p>Unggah {item.title}</p>
                  </div>
                </label>
              </>
            ) : (
              <div>
                <SuccessfulUploadComponent
                  title={item.title}
                  value={item.value}
                  type={item.type}
                  fileType={item.file_type}
                />
              </div>
            )}
          </div>
        );
      })}

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );
};

export default AddRegisterSarana;
